'use client'

import { CostOverTimeChart } from '@excellent/ui/blocks/lens/costs/cost-over-time-chart'
import { CostByProjectChart } from '@excellent/ui/blocks/lens/costs/cost-by-project-chart'
import { ModelTokenTable } from '@excellent/ui/blocks/lens/costs/model-token-table'
import { CacheEfficiencyPanel } from '@excellent/ui/blocks/lens/costs/cache-efficiency-panel'
import { formatCost } from '@lens/lib/decode'
import { formatUsd } from '@excellent/ui/format'
import { PRICING } from '@lens/lib/pricing'
import { Card, CardContent } from "@excellent/ui"
import { SectionTitle } from "@excellent/ui"
import { type StatStripItem } from "@excellent/ui"
import { Skeleton } from "@excellent/ui"
import { Alert, AlertDescription } from "@excellent/ui"
import { DataTable, type DataTableColumn } from "@excellent/ui"
import { AlertTriangle } from "@excellent/ui/icons"
import { useSnapshot } from '@lens/lib/use-snapshot'

interface PricingRow {
 model: string
 input: number
 output: number
 cacheWrite: number
 cacheRead: number
}

const pricingColumns: DataTableColumn<PricingRow>[] = [
 {
  key: 'model',
  header: 'Model',
  sort: (a, b) => a.model.localeCompare(b.model),
  render: (p) => <span className="type-body">{p.model}</span>,
 },
 {
  key: 'input',
  header: 'Input /MTok',
  sort: (a, b) => a.input - b.input,
  defaultDesc: true,
  render: (p) => pricePerMTok(p.input),
 },
 {
  key: 'output',
  header: 'Output /MTok',
  sort: (a, b) => a.output - b.output,
  defaultDesc: true,
  render: (p) => pricePerMTok(p.output),
 },
 {
  key: 'cacheWrite',
  header: 'Cache Write /MTok',
  sort: (a, b) => a.cacheWrite - b.cacheWrite,
  defaultDesc: true,
  render: (p) => pricePerMTok(p.cacheWrite),
 },
 {
  key: 'cacheRead',
  header: 'Cache Read /MTok',
  sort: (a, b) => a.cacheRead - b.cacheRead,
  defaultDesc: true,
  render: (p) => pricePerMTok(p.cacheRead),
 },
]

function PricingTable() {
 const rows: PricingRow[] = Object.entries(PRICING).map(([model, p]) => ({ model, ...p }))
 return (
  <DataTable
   wrapInCard={false}
   columns={pricingColumns}
   rows={rows}
   rowKey={(r) => r.model}
   initialSort={{ key: 'model', dir: 'asc' }}
   empty="No pricing data"
  />
 )
}

function pricePerMTok(rate: number): string {
 return formatUsd(rate * 1_000_000)
}

export default function CostsPage() {
 const { snapshot, error, isLoading } = useSnapshot()
 const data = snapshot?.costs
 const scopedProject = snapshot?.scopedProject ?? null

 return (
 <div className="flex flex-col">
 <div className="space-y-6">

 {error && (
 <Alert variant="destructive">
 <AlertTriangle className="h-4 w-4" />
 <AlertDescription>Error loading data: {String(error)}</AlertDescription>
 </Alert>
 )}

 {isLoading && (
 <div className="space-y-4">
 {/* Cost over time (title + stat band + graph) */}
 <Skeleton className="h-80 rounded-lg" />
 {/* Cost by project */}
 <Skeleton className="h-64 rounded-lg" />
 {/* Per-model + cache */}
 <Skeleton className="h-56 rounded-lg" />
 </div>
 )}

 {data && (() => {
 const costStats: StatStripItem[] = [
 {
 title: "Total Estimated Cost",
 value: formatCost(data.total_cost),
 description: scopedProject ? `Scoped to ${scopedProject.name}` : "All time spend across all projects",
 },
 {
 title: "Cache Savings",
 value: formatCost(data.total_savings),
 description: "Saved by prompt caching",
 },
 {
 title: "Without Cache",
 value: formatCost(data.total_cost + data.total_savings),
 description: "What you would have spent",
 },
 ]
 return (
 <>
 {/* Cost over time — self-contained hero card: title chrome + time-period
     dropdown (the single scope control), the cost totals banded across the
     head, then the stacked-area graph. */}
 <CostOverTimeChart
 daily={data.daily}
 stats={costStats}
 scopedProjectName={scopedProject?.name ?? null}
 />

 {/* Cost by project — meaningless when scoped to a single project. */}
 {!scopedProject && data.by_project.length > 0 && (
 <Card className="min-w-0 gap-0 overflow-hidden py-0">
 <SectionTitle variant="card" title="Cost by Project" />
 <CardContent className="py-4">
 <CostByProjectChart projects={data.by_project} />
 </CardContent>
 </Card>
 )}

 {/* Per-model table */}
 <Card className="min-w-0 gap-0 overflow-hidden py-0">
 <SectionTitle variant="card" title="Per-Model Token Breakdown" />
 <CardContent className="py-4">
 <ModelTokenTable models={data.models} />
 </CardContent>
 </Card>

 {/* Cache efficiency */}
 <Card className="min-w-0 gap-0 overflow-hidden py-0">
 <SectionTitle variant="card" title="Cache Efficiency" />
 <CardContent className="py-4">
 <CacheEfficiencyPanel models={data.models} totalSavings={data.total_savings} />
 </CardContent>
 </Card>

 {/* Pricing reference */}
 <Card className="min-w-0 gap-0 overflow-hidden py-0">
 <SectionTitle variant="card" title="Pricing Reference" />
 <CardContent className="py-4">
 <PricingTable />
 </CardContent>
 </Card>
 </>
 )
 })()}
 </div>
 </div>
 )
}
